import type { Agent, Facility, SafetyReviewItem } from '../types';
import { agents, facilities, flags } from './mockData';
import { agentPauses, callLimits, redFlagTriggers, reviewItems, SAFETY_SCENARIOS, testResults, type Scenario } from './safetyData';
import { getAgentScripts } from './clinicService';
import { addAudit, type Actor } from './auditService';
import { respond } from './respond';

export { SAFETY_SCENARIOS };

export const HARD_BLOCKS = [
  { key: 'sex', en: 'Sorry, we cannot share or discuss the baby\'s sex. It is against the law.', hi: 'माफ़ कीजिए, बच्चे के लिंग के बारे में हम बात नहीं कर सकते। यह क़ानून के ख़िलाफ़ है।' },
  { key: 'diagnosis', en: "I cannot say what the problem is. Please ask the doctor at your next OPD visit. I've noted this for the clinic.", hi: 'मैं बीमारी के बारे में नहीं बता सकती। कृपया अपनी अगली OPD विज़िट पर डॉक्टर से पूछिए। मैंने क्लिनिक के लिए यह नोट कर लिया है।' },
  { key: 'dose', en: "I cannot advise on medicines or doses. Please ask the doctor at your next OPD visit. I've noted this for the clinic.", hi: 'दवा या उसकी मात्रा के बारे में मैं नहीं बता सकती। कृपया अपनी अगली OPD विज़िट पर डॉक्टर से पूछिए। मैंने क्लिनिक के लिए यह नोट कर लिया है।' },
  { key: 'delay', en: 'It is best to get the vaccine on time. Please talk to the clinic before changing the date.', hi: 'टीका समय पर लगवाना सबसे अच्छा है। तारीख़ बदलनी हो तो क्लिनिक से बात करें।' },
  { key: 'fees', en: "I can't give fee details. Please ask at the clinic on your next OPD visit. I've noted this for the clinic.", hi: 'फ़ीस की जानकारी मैं नहीं दे सकती। कृपया अपनी अगली OPD विज़िट पर क्लिनिक में पूछ लीजिए। मैंने क्लिनिक के लिए यह नोट कर लिया है।' },
];

export const VOICE_DATA_FIELDS = ['firstName', 'doseName', 'dueDate', 'clinicName', 'doctorName'];

export async function getCallLimits() {
  return respond({ ...callLimits });
}

export async function saveCallLimits(v: typeof callLimits, actor?: Actor): Promise<void> {
  Object.assign(callLimits, v);
  addAudit(actor, 'limits.platform', 'Platform call limits', undefined, `${v.maxAttempts} attempts · ${v.maxCallMinutes} min · ${v.earliest}–${v.latest}`);
  return respond(undefined);
}

export async function getSafetySuite(): Promise<{ agent: Agent; runAt: string; results: Record<string, 'pass' | 'fail'> }[]> {
  return respond(agents.map((a) => ({ agent: a, ...testResults[a.scriptVersion] })));
}

// Counts only: no transcripts or patient details reach the super admin.
export async function getGuardrailEvents() {
  return respond(facilities.map((f) => {
    const items = reviewItems.filter((r) => r.facilityId === f.id);
    return {
      facility: f,
      blocked: items.filter((r) => r.kind === 'blocked_topic').length,
      redFlags: flags.filter((x) => x.facilityId === f.id && x.urgency === 'urgent').length,
      offScript: items.filter((r) => r.kind === 'off_script').length,
      pending: items.filter((r) => !r.verdict).length,
    };
  }));
}

export async function getRedFlagTriggers(facilityId: string) {
  return respond((redFlagTriggers[facilityId] ?? []).map((x) => ({ ...x })));
}

export async function saveRedFlagTriggers(facilityId: string, list: { id: string; en: string; hi: string; stage?: 'any' | 'pregnancy' | 'baby' | 'postpartum' }[], actor?: Actor): Promise<void> {
  if (actor?.role !== 'doctor') throw new Error('Only doctors can edit triggers');
  redFlagTriggers[facilityId] = list;
  addAudit(actor, 'triggers.saved', facilities.find((f) => f.id === facilityId)?.displayNameEn ?? facilityId, facilityId, `${list.length} triggers`);
  return respond(undefined);
}

export async function getReviewQueue(facilityId: string): Promise<SafetyReviewItem[]> {
  return respond(reviewItems.filter((r) => r.facilityId === facilityId).sort((a, b) => Number(!!a.verdict) - Number(!!b.verdict) || b.at.localeCompare(a.at)));
}

export async function setReviewVerdict(id: string, verdict: 'acceptable' | 'needs_fix', note: string, actor?: Actor): Promise<void> {
  if (actor?.role !== 'doctor') throw new Error('Only doctors can review');
  const r = reviewItems.find((x) => x.id === id);
  if (!r) return respond(undefined);
  Object.assign(r, { verdict, note: note.trim() || undefined, reviewedBy: actor.name, reviewedAt: new Date().toISOString() });
  if (verdict === 'needs_fix') agentPauses[`${r.facilityId}:${r.agentId}`] = { by: actor.name, at: r.reviewedAt!, note };
  addAudit(actor, `review.${verdict}`, `${r.facilityId} · ${r.agentId}`, r.facilityId, r.id);
  return respond(undefined);
}

export type AgentCallState = 'calling' | 'awaiting' | 'paused' | 'pausedTest' | 'clinicOff';
export interface AgentStatus { agent: Agent; state: AgentCallState; failed: Scenario[]; pause?: { by: string; at: string; note: string } }

// Pauses apply to one agent at one clinic only; other approved agents keep calling.
export async function getAgentCallingStatus(facilityId: string): Promise<AgentStatus[]> {
  const f = facilities.find((x) => x.id === facilityId);
  if (!f) return respond([]);
  const scripts = await getAgentScripts(facilityId);
  return respond(scripts.filter((a) => f.services[a.type]).map((a) => {
    const pause = agentPauses[`${facilityId}:${a.id}`];
    const failed = SAFETY_SCENARIOS.filter((s) => testResults[a.scriptVersion]?.results[s] === 'fail');
    const state: AgentCallState = pause ? 'paused' : a.status !== 'approved' ? 'awaiting' : failed.length ? 'pausedTest' : f.status !== 'active' ? 'clinicOff' : 'calling';
    return { agent: a, state, failed, pause };
  }));
}

export async function getAgentStatesByClinic(): Promise<{ facility: Facility; rows: AgentStatus[] }[]> {
  return respond(await Promise.all(facilities.map(async (facility) => ({ facility, rows: await getAgentCallingStatus(facility.id) }))));
}

export const SCENARIO_COUNT = SAFETY_SCENARIOS.length;
